export const ROLES = ['owner', 'admin', 'member'] as const;
export type Role = (typeof ROLES)[number];

export interface UserProfile {
  userId: string;
  email: string;
  displayName?: string;
}

export interface Organization {
  orgId: string;
  name: string;
  createdAt: number;
  createdBy: string;
}

export interface OrgSummary {
  orgId: string;
  name: string;
  role: Role;
  joinedAt: number;
}

export interface BotPromptExample {
  question: string;
  answer: string;
}

export interface BotPromptSections {
  role?: string;
  context?: string;
  tone?: string;
  rules?: string[];
  forbidden?: string[];
  tools?: string;
  escalation?: string;
  outputFormat?: string;
  examples?: BotPromptExample[];
  freeform?: string;
}

export interface Bot {
  orgId: string;
  botId: string;
  name: string;
  description?: string;
  systemPrompt?: string;
  promptSections?: BotPromptSections;
  llmProvider?: string;
  llmModel?: string;
  promptVersion?: number;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  updatedBy: string;
}

export type PromptVersionSource = 'create' | 'edit' | 'restore' | 'import';

export type PromptConfigField = keyof BotPromptSections | 'systemPrompt' | 'llmModel';

export interface PromptVersionSummary {
  botId: string;
  orgId: string;
  version: number;
  source: PromptVersionSource;
  llmModel?: string;
  changedFields: PromptConfigField[];
  note?: string;
  restoredFrom?: number;
  author?: string;
  createdAt: number;
  promptLength: number;
}

export interface PromptVersion extends Omit<PromptVersionSummary, 'promptLength'> {
  promptSections?: BotPromptSections;
  systemPrompt?: string;
}

export interface BotCreateInput {
  name: string;
  description?: string;
  systemPrompt?: string;
  promptSections?: BotPromptSections;
  llmProvider?: string;
  llmModel?: string;
  enabled?: boolean;
}

export type BotPatchInput = Partial<{
  name: string;
  description: string | null;
  systemPrompt: string | null;
  promptSections: BotPromptSections | null;
  llmProvider: string | null;
  llmModel: string | null;
  enabled: boolean;
  versionNote: string;
}>;

export interface Membership {
  userId: string;
  role: Role;
  joinedAt: number;
  invitedBy?: string;
  email?: string;
  displayName?: string;
  enabled?: boolean;
  missingUser?: boolean;
}

export interface MemberInviteInput {
  email: string;
  role: Exclude<Role, 'owner'>;
  displayName?: string;
  password?: string;
}

export interface MemberInviteResponse {
  user: UserProfile & { enabled: boolean };
  role: Role;
  generatedPassword?: string;
}

export const API_KEY_SCOPES = ['full', 'widget', 'mcp'] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

export interface BotApiKey {
  keyHash: string;
  orgId: string;
  botId: string;
  label: string;
  scope: ApiKeyScope;
  createdAt: number;
  createdBy: string;
  revokedAt?: number;
  lastUsedAt?: number;
}

export interface BotApiKeyWithToken extends BotApiKey {
  token: string;
}

export interface WidgetTheme {
  primaryColor: string;
  position: 'br' | 'bl';
}

export interface WidgetConfigView {
  enabled: boolean;
  allowedOrigins: string[];
  hmacSecretMasked: string;
  hmacSecretPreviousMasked: string | null;
  theme: WidgetTheme;
  launcherLabel: string;
  welcomeMessage: string;
  suggestions: string[];
  updatedAt: string;
  embedSnippetUrl: string;
}

export type WidgetConfigPatch = Partial<{
  enabled: boolean;
  allowedOrigins: string[];
  theme: WidgetTheme;
  launcherLabel: string;
  welcomeMessage: string;
  suggestions: string[];
}>;

export interface WidgetHmacRotateResponse {
  hmacSecret: string;
  view: WidgetConfigView;
}

export interface McpConfigView {
  enabled: boolean;
  updatedAt: string;
}

export type McpConfigPatch = Partial<{
  enabled: boolean;
}>;

export interface GoogleChatConfigView {
  enabled: boolean;
  spaceIds: string[];
  updatedAt: string;
}

export type GoogleChatConfigPatch = Partial<{
  enabled: boolean;
  spaceIds: string[];
}>;

export interface EscalationChannels {
  zoho: boolean;
  email: boolean;
}

export interface EscalationConfigView {
  enabled: boolean;
  channels: EscalationChannels;
  updatedAt: string;
}

export type EscalationConfigPatch = Partial<{
  enabled: boolean;
  channels: Partial<EscalationChannels>;
}>;

export interface FeedbackConfigView {
  enabled: boolean;
  updatedAt: string;
}

export type FeedbackConfigPatch = Partial<{
  enabled: boolean;
}>;

export const CACHE_TTL_MIN_SECONDS = 60;
export const CACHE_TTL_MAX_SECONDS = 24 * 60 * 60;

export interface CacheEndpointConfig {
  enabled: boolean;
  ttlSeconds: number;
}

export interface CacheConfigView {
  enabled: boolean;
  diagnose: CacheEndpointConfig;
  support: CacheEndpointConfig;
  generation: number;
  updatedAt: string;
}

export type CacheConfigPatch = Partial<{
  enabled: boolean;
  diagnose: Partial<CacheEndpointConfig>;
  support: Partial<CacheEndpointConfig>;
}>;

export interface SystemBot {
  botId: string;
  name: string;
  enabled: boolean;
  description?: string;
  llmProvider?: string;
  llmModel?: string;
}

export interface SystemSnapshot {
  version: string;
  uptimeSeconds: number;
  nodeEnv: 'development' | 'test' | 'production';
  bot: SystemBot | null;
  llm: { provider: string; model: string };
  rag: { provider: string; status: RagKnowledgeBaseStatus; knowledgeBaseId?: string; s3Bucket?: string } | null;
  integrations: IntegrationStatus[];
  tables: Record<string, string>;
  retention: {
    auditDays: number;
    feedbackDays: number;
    chatOpsDays: number;
    chatSupportDays: number;
  };
  chat: {
    maxHistoryTurns: number;
    toolResultReplayBytes: number;
  };
  cache: CacheConfigView & { hits: number; misses: number };
  dashboardOrigins: string[];
}

export const INTEGRATION_TYPES = [
  'elastic',
  'rag',
  'upela-api',
  'zoho',
  'ugo',
  'bigquery',
  'shipper-portal',
  'api',
  'email',
] as const;
export type IntegrationType = (typeof INTEGRATION_TYPES)[number];

export const INTEGRATION_KEYS = INTEGRATION_TYPES;
export type IntegrationKey = IntegrationType;

export interface IntegrationInstance {
  instanceId: string;
  type: IntegrationType;
  label: string;
  description?: string;
  config: Record<string, unknown>;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  lastTestAt?: string;
  lastTestStatus?: 'ok' | 'fail' | null;
  lastTestError?: string | null;
  toolsCount?: number;
}

export interface IntegrationTypeInfo {
  type: IntegrationType;
  label: string;
  description: string;
  maxInstances: number;
  currentCount: number;
  available: boolean;
  hasBundledTools: boolean;
}

export interface IntegrationStatus {
  instanceId: string;
  type: IntegrationType;
  label: string;
  description?: string;
  configured: boolean;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
  lastTestAt: string | null;
  lastTestStatus: 'ok' | 'fail' | null;
  lastTestError: string | null;
  config?: Record<string, unknown>;
  toolsCount?: number;
}

export interface ApiAuthConfig {
  type: 'none' | 'basic' | 'bearer' | 'api-key' | 'custom-headers';
  username?: string;
  password?: string;
  token?: string;
  headerName?: string;
  value?: string;
  headers?: Record<string, string>;
}

export interface ApiConfig {
  baseUrl: string;
  auth: ApiAuthConfig;
  defaultHeaders?: Record<string, string>;
  timeout: number;
  healthcheck?: {
    path: string;
    method: 'GET' | 'HEAD';
    expectedStatus: number;
  };
}

export interface ElasticConfig {
  url: string;
  username?: string;
  password?: string;
  apiKey?: string;
  defaultLogsIndex: string;
}
export type RagKnowledgeBaseStatus = 'provisioning' | 'active' | 'failed';

export interface RagConfig {
  provider: 'bedrock-kb';
  status: RagKnowledgeBaseStatus;
  knowledgeBaseId?: string;
  dataSourceId?: string;
  vectorIndexName?: string;
  error?: string;
}

export type RagKnowledgeBaseInfo =
  | { configured: false; envConfigured: boolean }
  | {
      configured: true;
      envConfigured: boolean;
      instanceId: string;
      enabled: boolean;
      status: RagKnowledgeBaseStatus;
      provisioningInFlight: boolean;
      knowledgeBaseId?: string;
      dataSourceId?: string;
      vectorIndexName?: string;
      error?: string;
    };
export interface UpelaApiConfig {
  baseUrl: string;
  username: string;
  password: string;
}
export interface ZohoConfig {
  region: ZohoRegion;
  orgId: string;
  departmentId: string;
  clientId: string;
  clientSecret: string;
  refreshToken: string;
}
export interface UgoConfig {
  baseUrl: string;
  apiKey: string;
}
export interface ShipperPortalConfig {
  baseUrl: string;
  username: string;
  password: string;
  userEmail: string;
}
export interface BigqueryConfig {
  projectId: string;
  keyJson: string;
  location: string;
  maxBytesBilled: number;
  defaultLimit: number;
  timeoutMs: number;
}
export interface EmailConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  fromAddress: string;
  toAddresses: string[];
}

export type IntegrationConfig = {
  elastic: ElasticConfig;
  rag: RagConfig;
  'upela-api': UpelaApiConfig;
  zoho: ZohoConfig;
  ugo: UgoConfig;
  bigquery: BigqueryConfig;
  'shipper-portal': ShipperPortalConfig;
  email: EmailConfig;
};

export const REDACTION_SENTINEL = '••••••••';

export interface PageResult<T> {
  items: T[];
  nextCursor?: string;
}

export const LANGUAGES = ['fr', 'en', 'es', 'it', 'de'] as const;
export type Language = (typeof LANGUAGES)[number];

export const ZOHO_REGIONS = ['eu', 'com', 'in', 'au', 'jp'] as const;
export type ZohoRegion = (typeof ZOHO_REGIONS)[number];

export interface ZohoCredentials {
  region: ZohoRegion;
  orgId: string;
  departmentId: string;
  clientId: string;
  clientSecret: string;
  refreshToken: string;
}

export type ToolSource = 'static' | 'dynamic';
export type ToolKind = 'elasticsearch' | 'bigquery' | 'api';

export type DynamicFieldType = 'string' | 'integer' | 'number' | 'boolean';

export interface DynamicInputField {
  type: DynamicFieldType | 'array';
  description?: string;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  items?: { type: DynamicFieldType };
}

export interface DynamicInputJsonSchema {
  type: 'object';
  properties: Record<string, DynamicInputField>;
  required?: string[];
}

export interface ToolCatalogItem {
  name: string;
  description: string;
  mutates: boolean;
  integration: IntegrationType;
  integrationId?: string;
  integrationLabel?: string;
  inputJsonSchema: Record<string, unknown>;
  source: ToolSource;
  enabled: boolean;
  kind?: ToolKind;
  target?:
    | { index: string }
    | { table: string; location?: string }
    | { path: string; method: string };
  template?: Record<string, unknown> | string | null;
  seedVersion?: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ToolRecord {
  toolName: string;
  description: string;
  inputJsonSchema: DynamicInputJsonSchema;
  enabled: boolean;
  integrationId: string;
  kind: ToolKind;
  target:
    | { index: string }
    | { table: string; location?: string }
    | { path: string; method: string };
  template: Record<string, unknown> | string | null;
  maxHits?: number;
  queryTemplate?: Record<string, string>;
  headers?: Record<string, string>;
  aiInstructions?: string;
  maxResponseBytes?: number;
  seedVersion?: number;
}

export interface PersistedToolRecord extends ToolRecord {
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface ToolTestResponse {
  ok: boolean;
  result?: unknown;
  error?: string;
}

export const AUDIT_STATUSES = [
  'answer_given',
  'answer_satisfied',
  'answer_escalated',
  'action_proposed',
  'action_succeeded',
  'action_failed',
  'action_failed_escalated',
  'action_rejected',
  'action_rejected_escalated',
  'error',
  'config_change',
] as const;
export type AuditStatus = (typeof AUDIT_STATUSES)[number];

export const AUDIT_CHANNELS = ['dashboard', 'widget', 'api', 'mcp', 'google-chat'] as const;
export type AuditChannel = (typeof AUDIT_CHANNELS)[number];

export interface AuditRecord {
  sessionId: string;
  chatSessionId: string | null;
  username: string;
  userId: string;
  orgId: string;
  botId: string;
  intent: string | null;
  channel: AuditChannel | null;
  origin: string | null;
  llmProvider: string;
  llmModel: string;
  promptVersion: number | null;
  prompt: string;
  reasoning: string | null;
  toolCalls: unknown;
  status: AuditStatus;
  response: string | null;
  actionName: string | null;
  actionSummary: string | null;
  proposalId: string | null;
  decidedAt: string | null;
  comment: string | null;
  executedAt: string | null;
  actionResult: string | null;
  error: string | null;
  escalatedAt: string | null;
  zohoTicketId: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
  cached: boolean | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: number;
}

export const FEEDBACK_STATUSES = ['pending', 'satisfied', 'escalated'] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export type EscalationChannel = 'zoho' | 'email';

export interface EscalationChannelOutcome {
  channel: EscalationChannel;
  ok: boolean;
  ref?: string;
  error?: string;
}

export interface FeedbackRecord {
  feedbackId: string;
  sessionId: string;
  username: string;
  userId: string;
  orgId: string;
  botId: string;
  channel?: AuditChannel;
  language: Language;
  originalPrompt: string;
  resolvedAnswer: string;
  shipmentId?: string;
  contactEmail?: string;
  category?: string;
  status: FeedbackStatus;
  createdAt: number;
  expiresAt: number;
  satisfiedAt?: number;
  escalatedAt?: number;
  escalatedChannels?: EscalationChannelOutcome[];
  escalatedSubject?: string;
  escalatedPriority?: 'Low' | 'Medium' | 'High';
  escalationComment?: string;
}

export const CHAT_FEEDBACK_RATINGS = ['up', 'down'] as const;
export type ChatFeedbackRating = (typeof CHAT_FEEDBACK_RATINGS)[number];

export const CHAT_FEEDBACK_CHANNELS = ['widget', 'dashboard', 'api'] as const;
export type ChatFeedbackChannel = (typeof CHAT_FEEDBACK_CHANNELS)[number];

export interface ChatMessageFeedback {
  sessionId: string;
  messageId: string;
  rating: ChatFeedbackRating;
  comment?: string;
  endUserId?: string;
  orgId: string;
  botId: string;
  channel: ChatFeedbackChannel;
  createdAt: number;
  expiresAt: number;
}

export const PROPOSAL_STATUSES = [
  'pending',
  'confirmed',
  'rejected',
  'executed',
  'failed',
] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export interface ActionFact {
  key: string;
  value: string;
}

export interface Proposal {
  proposalId: string;
  sessionId: string;
  channel?: AuditChannel;
  chatSessionId?: string;
  chatMessageId?: string;
  orgId: string;
  botId: string;
  userId: string;
  toolName: string;
  summary: string;
  payload: unknown;
  status: ProposalStatus;
  createdAt: number;
  expiresAt: number;
  confirmedAt?: number;
  executedAt?: number;
  result?: unknown;
  resultSummary?: string;
  resultFacts?: ActionFact[];
  error?: string;
  cancelledByAdmin?: boolean;
  cancelledBy?: string;
  cancelReason?: string;
}

export const RAG_SCOPES = ['ops', 'client', 'all'] as const;
export type RagScope = (typeof RAG_SCOPES)[number];

export interface RagDocumentMetadata {
  scope: RagScope;
  title?: string;
  tags?: string[];
  extra?: Record<string, string | number | boolean | string[]>;
}

export interface RagDocumentSummary {
  documentId: string;
  filename: string;
  s3Key: string;
  size: number;
  modifiedAt: string;
  metadata: RagDocumentMetadata;
  uploadedBy?: string;
  uploadedAt?: string;
}

export type RagDocumentDetail = RagDocumentSummary;

export type IngestionJobStatus =
  | 'STARTING'
  | 'IN_PROGRESS'
  | 'COMPLETE'
  | 'FAILED'
  | 'STOPPING'
  | 'STOPPED'
  | string;

export interface IngestionJobSummary {
  ingestionJobId: string;
  status: IngestionJobStatus;
  startedAt?: string;
  updatedAt?: string;
  statistics?: {
    documentsScanned?: number;
    newDocumentsIndexed?: number;
    modifiedDocumentsIndexed?: number;
    documentsDeleted?: number;
    documentsFailed?: number;
  };
  failureReasons?: string[];
}

export interface IngestionJobListResponse {
  jobs: IngestionJobSummary[];
  nextToken?: string;
}

export const INGESTION_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'COMPLETE',
  'FAILED',
  'STOPPED',
]);

export const CHAT_KINDS = ['ops', 'support'] as const;
export type ChatKind = (typeof CHAT_KINDS)[number];

export const CHAT_SESSION_STATUSES = ['active', 'closed', 'expired'] as const;
export type ChatSessionStatus = (typeof CHAT_SESSION_STATUSES)[number];

export type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMessageToolCall {
  callId: string;
  toolName: string;
  arguments: unknown;
  result?: unknown;
  error?: string;
  proposalId?: string;
}

export interface MessageProposalState {
  proposalId: string;
  status: ProposalStatus;
  summary: string;
  resultSummary?: string;
  resultFacts?: ActionFact[];
  error?: string;
}

export interface ChatStoredMessage {
  sessionId: string;
  messageId: string;
  role: ChatRole;
  content: string;
  toolCalls?: ChatMessageToolCall[];
  proposalId?: string;
  proposal?: MessageProposalState;
  feedbackId?: string;
  tokensIn?: number;
  tokensOut?: number;
  tokensCached?: number;
  createdAt: number;
}

export interface ChatSessionMeta {
  sessionId: string;
  kind: ChatKind;
  language: Language;
  orgId: string;
  botId: string;
  username: string;
  userId: string;
  title?: string;
  systemPromptHash: string;
  pinnedPromptVersion?: number;
  llmModel?: string;
  status: ChatSessionStatus;
  messageCount: number;
  lastMessageId: string | null;
  tokensIn: number;
  tokensOut: number;
  tokensCached: number;
  createdAt: number;
  lastActivityAt: number;
  expiresAt: number;
}

export interface ChatSessionDetail {
  session: ChatSessionMeta;
  messages: PageResult<ChatStoredMessage>;
}

export type ChatTurnDecision =
  | { kind: 'resolved'; answer: string }
  | {
      kind: 'proposal';
      proposal: { proposalId: string; toolName: string; summary: string; payload: unknown };
      summary: string;
    }
  | { kind: 'escalated'; ticketId: string; summary: string }
  | { kind: 'failed'; reason: string };

export interface ChatTurnUsage {
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheCreationInputTokens: number;
}

export interface ChatTurnResponse {
  sessionId: string;
  userMessage: ChatStoredMessage;
  assistantMessage: ChatStoredMessage;
  decision: ChatTurnDecision;
  usage: ChatTurnUsage;
  lastMessageId?: string;
}

export type AgentEvent =
  | { type: 'iteration_start'; iteration: number }
  | { type: 'llm_response'; iteration: number; hasText: boolean; toolCallCount: number }
  | { type: 'tool_call_start'; name: string; arguments: unknown }
  | { type: 'tool_call_end'; name: string; result?: unknown; error?: string }
  | { type: 'proposal'; proposalId: string; summary: string }
  | { type: 'assistant_text'; text: string };

export interface DiagnoseInput {
  shipmentId?: string;
  orderId?: string;
  trackingNumber?: string;
  context?: string;
  promptVersion?: number;
}

export interface SupportInput {
  message: string;
  shipmentId?: string;
  contactEmail?: string;
  category?: string;
  promptVersion?: number;
}

export type ActionDecision = ChatTurnDecision;

export interface FeedbackAction {
  type: 'satisfied' | 'escalate';
  label: string;
  method: 'POST';
  url: string;
}

export interface FeedbackBlock {
  feedbackId: string;
  question: string;
  expiresAt: string;
  actions: FeedbackAction[];
}

export interface DiagnoseResponse {
  sessionId: string;
  decision: ActionDecision;
  usage: ChatTurnUsage;
  latencyMs: number;
}

export interface SupportResponse extends DiagnoseResponse {
  feedback?: FeedbackBlock;
}

export interface FeedbackSatisfiedResponse {
  status: 'satisfied';
  feedbackId: string;
}

export interface FeedbackEscalateResponse {
  status: 'escalated';
  feedbackId: string;
  subject: string;
  priority: 'Low' | 'Medium' | 'High';
  channels: EscalationChannelOutcome[];
  ticketId?: string;
}
